import { useTranslation } from 'react-i18next';
import { P2GGrid, P2GIcon, SvgArrowLinkLeft, SvgArrowLinkRight, SvgCalendarFull } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { useMemo } from 'react';
import { formateDate } from '~/lib/utils/date-utils';
import { ProductItem } from '../adapters/product-types';
import { ProductItemAddress } from './address/product-item-address';
import { ProductItemDate } from './date/product-item-date';
import { ProductItemShop } from './shop/product-item-shop';
import { BasketSections } from '~/lib/types/enums';

type ProductItemLocationsProps = {
  productItem: ProductItem;
};

export default function ProductItemLocations({ productItem }: ProductItemLocationsProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { origin, destination, dates, service } = productItem;

  const collectionOrReturnTitle = productItem.service.isDropOffService
    ? `${t('CONFIRM_RETURN_DETAILS_TITLE', { defaultValue: 'Return Details' })}:`
    : `${t('CONFIRM_COLLECTION_ADDRESS_TITLE', { defaultValue: 'Collection Address' })}:`;

  const formattedCollectionDate = useMemo(() => {
    return dates.collectionDate
      ? formateDate(
          t,
          new Date(dates.collectionDate),
          dates.collectionDateCutoff ? new Date(dates.collectionDateCutoff) : null
        )
      : '';
  }, [dates, t]);

  const deliveryDate = useMemo(() => {
    if (dates.deliveryDateMin && dates.deliveryDateMax) {
      if (dates.deliveryDateMin === dates.deliveryDateMax) {
        return formateDate(t, new Date(dates.deliveryDateMin), null);
      }
      return `${formateDate(t, new Date(dates.deliveryDateMin))} - ${formateDate(t, new Date(dates.deliveryDateMax), null)}`;
    }
    return dates.estimatedDeliveryDate ? formateDate(t, new Date(dates.estimatedDeliveryDate), null) : '';
  }, [dates, t]);

  return (
    <P2GGrid container direction="column" spacing={3} flexWrap="nowrap">
      <P2GGrid item>
        <ProductItemAddress
          title={collectionOrReturnTitle}
          address={origin}
          icon={
            <P2GIcon size="sm" palette="secondary">
              <SvgArrowLinkLeft />
            </P2GIcon>
          }
        />
      </P2GGrid>
      {!productItem.service.isDropOffService && (
        <P2GGrid item>
          <ProductItemDate
            title={t('CONFIRM_COLLECTION_DATE_TITLE', { defaultValue: 'Collection Date:' })}
            date={formattedCollectionDate}
            icon={
              <P2GIcon size="sm" palette="success">
                <SvgCalendarFull />
              </P2GIcon>
            }
          />
        </P2GGrid>
      )}
      <P2GGrid item>
        <ProductItemAddress
          title={t('CONFIRM_DELIVERY_DETAILS_TITLE', { defaultValue: 'Delivery Details:' })}
          address={destination}
          icon={
            <P2GIcon size="sm" palette="secondary">
              <SvgArrowLinkRight />
            </P2GIcon>
          }
        />
      </P2GGrid>
      {deliveryDate && (
        <P2GGrid item>
          <ProductItemDate
            title={t('CONFIRM_ESTIMATED_DELIVERY_TITLE', { defaultValue: 'Estimated Delivery:' })}
            date={deliveryDate}
            icon={
              <P2GIcon size="sm" palette="success">
                <SvgCalendarFull />
              </P2GIcon>
            }
          />
        </P2GGrid>
      )}
      {service.isDropOffService && origin.shop && (
        <P2GGrid item>
          <ProductItemShop
            quoteOffersPrintInStore={productItem.quoteOffersPrintInStore}
            shop={origin.shop}
            type={BasketSections.ORIGIN}
          />
        </P2GGrid>
      )}
      {service.isShopDelivery && destination.shop && (
        <P2GGrid item>
          <ProductItemShop
            quoteOffersPrintInStore={productItem.quoteOffersPrintInStore}
            shop={destination.shop}
            type={BasketSections.DESTINATION}
          />
        </P2GGrid>
      )}
    </P2GGrid>
  );
}
